import {
  extent,
  linearScale,
  niceTicks,
  paddedDomain,
  type SeriesPoint,
  splitOnGaps,
} from '~/calculations/chartScale'
import styles from './LineChart.module.css'

/**
 * A trend line, drawn by hand (D-087).
 *
 * **Straight segments between real observations, and a visible break where
 * there are none.** No smoothing: a curve through weekly weigh-ins draws values
 * on days that were never measured, in an app whose engine refuses to treat a
 * missing value as a zero and whose export distinguishes absent from excluded
 * in every block. A chart that invents intermediate values contradicts that
 * everywhere it appears.
 *
 * **The chart is never the only representation of its data.** Every instance
 * carries a textual summary, visible rather than screen-reader-only, because
 * the numbers are what the user is actually after and a sighted user reading a
 * 340 px chart on a phone benefits from them just as much. That requirement is
 * also what makes a hand-rolled renderer acceptable: if the drawing is wrong,
 * the sentence beside it is still right.
 */

const WIDTH = 340
const HEIGHT = 150
const PADDING = { top: 8, right: 8, bottom: 20, left: 34 }

/** Observations further apart than this break the line rather than bridge it. */
const MAX_GAP_DAYS = 10

export interface ChartBoundary {
  /** Same x units as the series — days from the series origin. */
  readonly x: number
  readonly label: string
}

interface LineChartProps {
  readonly title: string
  readonly points: readonly SeriesPoint[]
  /** Rendered into tick labels and the summary. Formatting stays out of here. */
  readonly formatValue: (value: number) => string
  /** The sentence beneath the chart. Required — see the note above. */
  readonly summary: string
  readonly xStartLabel?: string
  readonly xEndLabel?: string
  /** Phase or project boundaries, drawn as vertical markers. */
  readonly boundaries?: readonly ChartBoundary[]
  /** An optional dashed continuation. Never styled like an observation. */
  readonly projection?: readonly SeriesPoint[]
  readonly emptyMessage?: string
}

export function LineChart({
  title,
  points,
  formatValue,
  summary,
  xStartLabel,
  xEndLabel,
  boundaries = [],
  projection,
  emptyMessage = 'Not enough data to draw this yet.',
}: LineChartProps) {
  const observed = extent(points)

  if (observed === undefined) {
    return (
      <figure class={styles.figure}>
        <figcaption class={styles.title}>{title}</figcaption>
        <p class={styles.empty}>{emptyMessage}</p>
      </figure>
    )
  }

  /* The projection shares the axes, so its values widen the domain — otherwise
     a target below every recorded weight would be drawn outside the frame. */
  const projected = extent(projection ?? [])
  const low = Math.min(observed[0], projected?.[0] ?? observed[0])
  const high = Math.max(observed[1], projected?.[1] ?? observed[1])

  const allX = [...points, ...(projection ?? [])].map((point) => point.x)
  const x = linearScale(
    [Math.min(...allX), Math.max(...allX)],
    [PADDING.left, WIDTH - PADDING.right],
  )
  const y = linearScale(paddedDomain(low, high), [HEIGHT - PADDING.bottom, PADDING.top])

  const ticks = niceTicks(y.domain[0], y.domain[1], 4)
  const segments = splitOnGaps(points, MAX_GAP_DAYS)

  const path = (segment: readonly SeriesPoint[]) =>
    segment.map((point) => `${x.at(point.x).toFixed(2)},${y.at(point.y).toFixed(2)}`).join(' ')

  return (
    <figure class={styles.figure}>
      <figcaption class={styles.title}>{title}</figcaption>

      {/* `role="img"` with a label, because the shape carries no information the
          summary below does not state in words. */}
      <svg
        class={styles.svg}
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        role="img"
        aria-label={`${title}. ${summary}`}
        preserveAspectRatio="none"
      >
        <title>{title}</title>

        {ticks.map((tick) => (
          <g key={`tick-${tick}`}>
            <line
              class={styles.grid}
              x1={PADDING.left}
              x2={WIDTH - PADDING.right}
              y1={y.at(tick)}
              y2={y.at(tick)}
            />
            <text class={styles.axisText} x={0} y={y.at(tick) + 3}>
              {formatValue(tick)}
            </text>
          </g>
        ))}

        {boundaries.map((boundary) => (
          <line
            key={`boundary-${boundary.x}-${boundary.label}`}
            class={styles.boundary}
            x1={x.at(boundary.x)}
            x2={x.at(boundary.x)}
            y1={PADDING.top}
            y2={HEIGHT - PADDING.bottom}
          />
        ))}

        {projection === undefined || projection.length < 2 ? null : (
          <polyline class={styles.projection} points={path(projection)} fill="none" />
        )}

        {segments.map((segment) =>
          segment.length === 1 ? (
            /* A lone observation is a dot. Drawing nothing would hide a real
               reading, and drawing a line of length zero shows nothing either. */
            <circle
              key={`point-${segment[0]?.x}`}
              class={styles.point}
              cx={x.at(segment[0]?.x ?? 0)}
              cy={y.at(segment[0]?.y ?? 0)}
              r={2.5}
            />
          ) : (
            <polyline
              key={`segment-${segment[0]?.x}`}
              class={styles.line}
              points={path(segment)}
              fill="none"
            />
          ),
        )}
      </svg>

      <div class={styles.axisLabels}>
        <span>{xStartLabel ?? ''}</span>
        <span>{xEndLabel ?? ''}</span>
      </div>

      {/* Visible, not screen-reader-only. The numbers are the point. */}
      <p class={styles.summary}>{summary}</p>

      {boundaries.length === 0 ? null : (
        <p class={styles.legend}>
          {/* Never colour alone: the marker is named in words. */}
          Vertical marks: {boundaries.map((boundary) => boundary.label).join(', ')}.
        </p>
      )}
    </figure>
  )
}
